import { useState } from "react"
import { useForm } from "react-hook-form"
import { api } from "../core"
import { useAccessContext } from "../component/guard/AccessGuard"
import { ManagedFloatingSelect } from "popopo-lib-front/component"

type HomeDisplaySpace = {
  space: {
    spaceKey: string,
    name: string,
    userId: string,
    bgm: {
      id: string | null,
      kind: string,
      volume: number,
    }
    background: {
      id: string,
      kind: string,
    }
    onlineUsers: string[],
    lastSelectedParticipantUserId?: string,
    nonMuteOnlineUserCount: number
  },
  live?: {
    id: string,
    spaceKey: string,
    userId: string,
    token: string | null,
    genreId: string,
    tags: string[],
    canEnter: boolean,
    currentCount: number,
    selectionRecruiting: boolean,
  },
  thumbnail: {
    lookImages: {
      imageUrl: string,
      createdAt: number,
    }[],
    size: "small" | "medium" | "large",
    distance: "middle" | "far" | "near",
    overflowHeight: "small" | "medium" | "large",
    backgroundEffectIntensity: "strong" | "mild" | "weak",
  }
}

type HomeDisplaySpaceGetForms = {
  category: "recommend" | "follow" | "first-launch",
  kind: "spec-defined" | "all" | "dummy"
}

export const Home = () => {
  const context = useAccessContext()
  const { credential } = context
  const [spaces, setSpaces] = useState<HomeDisplaySpace[]>([])
  const [loading, setLoading] = useState(false)

  const form = useForm<HomeDisplaySpaceGetForms>({
    defaultValues: {
      category: "recommend",
      kind: "spec-defined"
    }
  })
  const { isValid } = form.formState

  const onSubmit = async (value: HomeDisplaySpaceGetForms) => {
    setLoading(true)

    try {
      const token = await credential.user.getIdToken()
      const response = await api.POST("/api/v2/users/me/home-display-spaces", {
        body: {
          category: value.category,
          kind: value.kind,
        },
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      if (response.error) {
        console.error(response.error)
      } else {
        setSpaces(response.data.spaces)
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="row gy-5 justify-content-center">
      <div className="d-flex col-12 col-xl-6 flex-column gap-3 align-items-center">

        <h3>Home API</h3>

        <form className="d-flex w-100 flex-column gap-3" onSubmit={form.handleSubmit(onSubmit)}>
          <ManagedFloatingSelect control={form.control} label="category" name="category" options={
            ["recommend", "follow", "first-launch"].map((x) => ({
              value: x,
              label: x,
            }))
          }/>
          <ManagedFloatingSelect control={form.control} label="kind" name="kind" options={
            ["spec-defined", "all", "dummy"].map((x) => ({
              value: x,
              label: x,
            }))
          }/>
          <button type="submit" className="btn btn-primary form-control" disabled={loading || !isValid}>
            Call &quot;POST /users/me/home-display-spaces&quot; API
          </button>
        </form>

        <h3>Spaces</h3>

        {spaces.map((item, index) => (
          <div className="col-lg-4 col-md-6 mb-3" key={index}>
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">{item.space.name}</h5>
                <div className="card-text">
                  <strong>Space Key:</strong> {item.space.spaceKey}<br />
                  <strong>Online User Count:</strong> {item.space.onlineUsers.length}<br />
                  <strong>Owner ID:</strong> {item.space.userId}<br />
                  {item.live && (
                    <>
                      <strong>Live ID:</strong> {item.live.id}<br />
                      <strong>Live Genre:</strong> {item.live.genreId}<br />
                      <strong>Current Count:</strong> {item.live.currentCount}<br />
                    </>
                  )}
                  <strong>Thumbnail:</strong><br />
                  {0 < item.thumbnail.lookImages.length ? (
                    <div className="mt-2">
                      {item.thumbnail.lookImages.map((image, imageIndex) => (
                        <div key={imageIndex} className="mb-2">
                          <div className="text-break">{image.imageUrl}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-muted">Look Image Not Generated</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
